import { CSSProperties, useMemo, useState } from "react";

import { Button } from "@blueprintjs/core";
import throttle from "lodash/throttle";
import { useMediaQuery } from "react-responsive";

import { Flex } from "@/components/layout/flexbox";
import { isXsQuery, Media } from "@/components/layout/Media";
import { SearchBox } from "@/components/pages_logged_in/workspace-admin/SearchBox";
import { Filter } from "@/components/pieces/filter/Filter";
import { FilterButton } from "@/components/pieces/filter/FilterButton";
import { SettingSvg } from "@/components/pieces/icons/svg/setting";
import { ScopeFilterOption } from "@/types/roleplays";

import { CreateCollectionDialog } from "./CreateCollectionDialog";

interface RoleplayListHeaderProps {
  selectedScopes: ScopeFilterOption[];
  scopes: ScopeFilterOption[];
  onChangeScope: (scope: ScopeFilterOption) => void;
  onSearch: (value: string) => void;
  style?: CSSProperties;
  loading?: boolean;
  onCreateCollectionFinished: () => void;
  allowAdd?: boolean;
}

export function RoleplayListHeader({
  selectedScopes,
  scopes,
  onChangeScope,
  onSearch,
  style = {},
  loading = false,
  onCreateCollectionFinished,
  allowAdd = false,
}: RoleplayListHeaderProps) {
  const [isOpenCreateDialog, setIsOpenDialog] = useState(false);

  const scopeMenuLabel = useMemo(() => {
    const selectedScopeIdsNoViewAll = selectedScopes;
    if (selectedScopeIdsNoViewAll.length === 1) {
      return selectedScopes[0]?.name;
    } else if (selectedScopeIdsNoViewAll.length === 0) {
      return "None Selected";
    }

    return `${selectedScopeIdsNoViewAll.length} Scopes`;
  }, [selectedScopes]);

  const isXs = useMediaQuery(isXsQuery);

  const borderStyle = {
    border: "0.5px solid #E2E2E2",
    marginRight: 10,
    padding: "10px 24px",
    minWidth: isXs ? 80 : 150,
    width: "100%",
  };

  return (
    <>
      <Media greaterThanOrEqual="lg">
        <Flex
          fontSize={24}
          fontWeight={600}
          mt={4}
          mb={3}
          justifyContent="space-between"
        >
          <Flex className="gap-2">
            <Filter
              button={
                <FilterButton
                  style={{ ...style, ...borderStyle }}
                  rightIcon={<SettingSvg />}
                  text={scopeMenuLabel}
                  disabled={loading}
                />
              }
              options={scopes}
              selectedOptions={selectedScopes}
              multiple={false}
              onFilterChange={(option) =>
                throttle(() => onChangeScope(option), 200)()
              }
              menuPosition="bottom-start"
              viewMode="radio"
              fullHeight={true}
            />
            <SearchBox
              style={{
                borderRadius: 6,
                border: "0.5px solid #E2E2E2",
                overflow: "hidden",
                padding: "22px 0px 22px 36px",
                fontSize: 16,
              }}
              className="icon-center"
              disabled={loading}
              refine={onSearch}
              throttledDuration={400}
              searchPlaceholder="Search Collections..."
            />
          </Flex>

          {allowAdd && (
            <Button
              intent="primary"
              icon="plus"
              style={{ fontSize: 15, marginLeft: 10 }}
              onClick={() => setIsOpenDialog(true)}
            >
              Create Collection
            </Button>
          )}
        </Flex>
      </Media>

      <Media lessThan="lg">
        <Flex className="gap-2" flexDirection="column" mt={4} mb={3}>
          <Flex
            fontSize={24}
            fontWeight={600}
            justifyContent="space-between"
            style={{ width: "100%" }}
            className="gap-2"
          >
            <Filter
              className="flex-1!"
              button={
                <FilterButton
                  style={{ ...style, ...borderStyle }}
                  rightIcon={<SettingSvg />}
                  text={scopeMenuLabel}
                  disabled={loading}
                />
              }
              options={scopes}
              selectedOptions={selectedScopes}
              multiple={false}
              onFilterChange={(option) =>
                throttle(() => onChangeScope(option), 200)()
              }
              menuPosition="bottom-start"
              viewMode="radio"
              fullHeight={true}
            />

            {allowAdd && (
              <Button
                className="flex-1!"
                intent="primary"
                icon="plus"
                style={{ fontSize: 15 }}
                onClick={() => setIsOpenDialog(true)}
              >
                Create Collection
              </Button>
            )}
          </Flex>
          <SearchBox
            style={{
              borderRadius: 6,
              border: "0.5px solid #E2E2E2",
              overflow: "hidden",
              padding: "22px 0px 22px 36px",
              fontSize: 16,
            }}
            className="icon-center"
            disabled={loading}
            refine={onSearch}
            throttledDuration={400}
            searchPlaceholder="Search Collections..."
          />
        </Flex>
      </Media>

      <CreateCollectionDialog
        isOpen={isOpenCreateDialog}
        onClose={() => setIsOpenDialog(false)}
        onFinished={onCreateCollectionFinished}
      />
    </>
  );
}
